import { Button } from "@/components/ui/button.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { Info } from "lucide-react";
import { useState } from "react";

type ToggleComponentData = Extract<ArtifactComponent, { type: "toggle" }>;

export const TogglePillBlock = ({ component }: { component: ToggleComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Card style: full-width bordered row with the help text shown inline,
  // matching the legacy permission toggle.
  if (component.style === "card") {
    return (
      <div className="flex w-full items-center justify-between gap-3 rounded-lg border p-3">
        <div className="text-left">
          <div className="text-sm font-medium">{component.label}</div>
          {component.helpText ? (
            <div className="mt-0.5 text-xs text-muted-foreground">{component.helpText}</div>
          ) : null}
        </div>
        <Toggle
          checked={component.value}
          onCheckedChange={(checked) => {
            void patchOne(component.editPath, checked);
          }}
          disabled={!canEdit}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 shrink-0 text-xs text-muted-foreground">
      <span>{component.label}</span>
      {component.helpText ? (
        <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-statsig-name="artifact-view-toggle-info"
              onClick={() => setTooltipOpen((o) => !o)}
              className="h-4 w-4 p-0"
            >
              <Info className="h-3 w-3 text-muted-foreground/60" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64 text-xs">
            {component.helpText}
          </TooltipContent>
        </Tooltip>
      ) : null}
      <Toggle
        checked={component.value}
        onCheckedChange={(checked) => {
          void patchOne(component.editPath, checked);
        }}
        disabled={!canEdit}
        leftLabel={component.leftLabel ?? "Off"}
        rightLabel={component.rightLabel ?? "On"}
      />
    </div>
  );
};
